import { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { parseSpreadsheetFile } from './parse';
import { autoDetectMapping } from './matching';
import { ENTITY_CONFIGS } from './entityConfigs';
import { downloadCsv } from './csvExport';
import { C } from '../Dashboard';

// This wizard is a permanently-dark full-screen modal overlay regardless of
// the org's live theme (same treatment as ImportHistory.js's file-viewer
// modal) -- it never inherits the page's own background, so bg/surface/
// border/muted/bright/danger/success stay fixed on purpose. The one
// exception is accent: it's used on the wizard's own primary action buttons
// ("Continue", "Import N rows") and the Update badge, both of which should
// still match a customer's chosen accent color like every other primary
// button in the app -- so accent reads from the live C token, not W.
const W = { bg: '#0A1526', surface: '#16233F', border: '#28395C', muted: '#7d8ea2', bright: '#f1f5f9', danger: '#f87171', success: '#68d391', amber: '#f6c26b' };
const btn = (variant) => ({
  padding: '8px 18px', borderRadius: '7px', border: variant === 'ghost' ? `1px solid ${W.border}` : 'none',
  background: variant === 'primary' ? C.accent : variant === 'ghost' ? 'transparent' : W.border,
  color: variant === 'primary' ? '#fff' : variant === 'ghost' ? W.muted : W.bright,
  fontSize: '13px', fontWeight: '600', cursor: 'pointer',
});
const sel = { padding: '6px 8px', borderRadius: '6px', border: `1px solid ${W.border}`, background: W.bg, color: W.bright, fontSize: '12px' };
const IGNORE = '__ignore__';

function Badge({ action }) {
  // Built at render time (not module scope) so the Update badge's accent
  // color tracks the live C token -- a module-level object here would
  // freeze whatever C looked like at bundle-load, before applyTheme ever ran.
  const actionBadge = {
    create: { label: 'Create', bg: 'rgba(104,211,145,0.12)', color: W.success },
    update: { label: 'Update', bg: 'rgba(0,160,200,0.12)', color: C.accent },
    skip: { label: 'Skip', bg: 'rgba(125,142,162,0.15)', color: W.muted },
    error: { label: 'Error', bg: 'rgba(248,113,113,0.12)', color: W.danger },
  };
  const a = actionBadge[action];
  return <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px', background: a.bg, color: a.color, whiteSpace: 'nowrap' }}>{a.label}</span>;
}

export default function BulkImportModal({ entityType, org, profile, onClose, onComplete }) {
  const config = ENTITY_CONFIGS[entityType];
  const [step, setStep] = useState('upload'); // upload | mapping | preview | importing | results
  const [fileName, setFileName] = useState('');
  const [rawFile, setRawFile] = useState(null); // the actual File — kept so it can be logged to import_logs
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [ctx, setCtx] = useState(null);
  const [ctxError, setCtxError] = useState('');
  const [parseErr, setParseErr] = useState('');
  const [mapErr, setMapErr] = useState('');
  const [rowState, setRowState] = useState({}); // { [index]: { include, actionOverride } }
  const [results, setResults] = useState(null); // { rows: [...], summary }
  const fileInputRef = useRef(null);

  useEffect(() => {
    config.loadContext(org).then(setCtx).catch(e => setCtxError(e.message || 'Failed to load org data'));
  }, []);

  const handleFile = async (file) => {
    setParseErr('');
    try {
      const { headers: h, rows } = await parseSpreadsheetFile(file);
      if (!h.length) { setParseErr('That file has no rows.'); return; }
      setFileName(file.name);
      setRawFile(file);
      setHeaders(h);
      setRawRows(rows);
      const detected = autoDetectMapping(h, config.fields);
      const full = {};
      h.forEach((_, i) => { full[i] = detected[i] || IGNORE; });
      setMapping(full);
      setStep('mapping');
    } catch (e) {
      setParseErr(e.message);
    }
  };

  const proceedToPreview = () => {
    const mappedFieldKeys = new Set(Object.values(mapping).filter(v => v !== IGNORE));
    const missing = config.fields.filter(f => f.required && !mappedFieldKeys.has(f.key));
    if (missing.length) { setMapErr(`Map a column to: ${missing.map(f => f.label).join(', ')}`); return; }
    setMapErr('');
    setStep('preview');
  };

  const previewRows = useMemo(() => {
    if (step !== 'preview' && step !== 'importing' && step !== 'results') return [];
    if (!ctx) return [];
    const seen = new Set();
    return rawRows.map((row, index) => {
      const values = {};
      headers.forEach((_, colIndex) => {
        const key = mapping[colIndex];
        if (key && key !== IGNORE) values[key] = row[colIndex];
      });
      const built = config.buildPreviewRow(values, ctx, seen);
      return { index, rawValues: values, ...built };
    });
  }, [step, ctx]);

  // Initialize per-row include/override state once preview rows are computed.
  useEffect(() => {
    if (step !== 'preview' || !previewRows.length) return;
    setRowState(prev => {
      if (Object.keys(prev).length) return prev;
      const next = {};
      previewRows.forEach(r => {
        next[r.index] = { include: r.errors.length === 0 && r.defaultAction !== 'skip', actionOverride: null };
      });
      return next;
    });
  }, [step, previewRows.length]);

  const effectiveRows = useMemo(() => previewRows.map(r => {
    const rs = rowState[r.index] || { include: false, actionOverride: null };
    const displayAction = r.errors.length ? 'error' : (rs.actionOverride || r.defaultAction);
    return { ...r, include: r.errors.length ? false : rs.include, displayAction, effectiveAction: (r.errors.length || !rs.include) ? 'none' : displayAction };
  }), [previewRows, rowState]);

  const includedCount = effectiveRows.filter(r => r.effectiveAction !== 'none').length;

  const toggleInclude = (index) => setRowState(s => ({ ...s, [index]: { ...s[index], include: !s[index]?.include } }));
  const toggleOverride = (index) => setRowState(s => {
    const current = s[index] || { include: true, actionOverride: null };
    const next = current.actionOverride === 'update' ? null : 'update';
    return { ...s, [index]: { include: true, actionOverride: next } };
  });

  const runImport = async () => {
    setStep('importing');
    const toCommit = effectiveRows.map(r => ({ ...r, effectiveAction: r.effectiveAction === 'none' ? 'skip' : r.effectiveAction }));
    const commitResults = await config.commit(toCommit, org, profile);
    const rowsOut = effectiveRows.map((r, i) => {
      if (r.effectiveAction === 'none') return { ...r, status: 'skipped' };
      const res = commitResults[i] || { status: 'error', reason: 'Unknown error' };
      return { ...r, status: res.status, statusReason: res.reason };
    });
    const summary = rowsOut.reduce((acc, r) => {
      if (r.status === 'created') acc.created++;
      else if (r.status === 'updated') acc.updated++;
      else if (r.status === 'error') acc.errors++;
      else acc.skipped++;
      return acc;
    }, { created: 0, updated: 0, skipped: 0, errors: 0 });
    setResults({ rows: rowsOut, summary });
    setStep('results');
    onComplete?.();
    logImport(summary, rowsOut.length).catch(e => console.error('import_logs write failed:', e));
  };

  // Best-effort: the import itself already succeeded or failed by the time this
  // runs, so a logging failure is logged and swallowed rather than shown as an
  // import error — the admin's data is already committed either way.
  const logImport = async (summary, totalRows) => {
    if (!rawFile) return;
    const path = `${org.id}/${crypto.randomUUID()}-${rawFile.name}`;
    const { error: upErr } = await supabase.storage.from('import-files').upload(path, rawFile);
    if (upErr) throw upErr;
    const status = summary.errors === 0 ? 'success' : (summary.created + summary.updated === 0 ? 'failed' : 'partial');
    const { error: logErr } = await supabase.from('import_logs').insert({
      org_id: org.id, user_id: profile.id, entity_type: entityType,
      file_name: rawFile.name, file_path: path, status, total_rows: totalRows,
      created_count: summary.created, updated_count: summary.updated,
      skipped_count: summary.skipped, error_count: summary.errors,
    });
    if (logErr) throw logErr;
  };

  const downloadTemplate = () => {
    const row = {};
    config.fields.forEach(f => { row[f.label] = f.example ?? ''; });
    downloadCsv(`${entityType}-import-template.csv`, [row]);
  };

  const downloadErrorRows = () => {
    const errRows = (results?.rows || []).filter(r => r.status === 'error' || r.errors.length);
    if (!errRows.length) return;
    // Export what was actually in the uploaded file (rawValues), not our internal field
    // names — some (e.g. "client" -> client_id, "paid_date" -> paid_at) don't match 1:1,
    // and the admin fixing this file wants to see their own columns back, not ours.
    const fieldKeys = config.fields.map(f => f.key);
    downloadCsv(`${entityType}-import-errors.csv`, errRows.map(r => {
      const rowOut = {};
      fieldKeys.forEach(k => { rowOut[k] = r.rawValues[k] ?? ''; });
      rowOut.error_reason = r.errors.length ? r.errors.join('; ') : (r.statusReason || 'Import failed');
      return rowOut;
    }));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: '20px' }}>
      <div style={{ background: W.surface, border: `1px solid ${W.border}`, borderRadius: '14px', width: '100%', maxWidth: step === 'preview' || step === 'results' ? '920px' : '560px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${W.border}`, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '17px', fontWeight: '800', color: W.bright }}>Import {config.label} from a spreadsheet</div>
            <div style={{ fontSize: '12px', color: W.muted, marginTop: '3px' }}>
              {{ upload: 'Step 1 — Upload', mapping: 'Step 2 — Map columns', preview: 'Step 3 — Review', importing: 'Importing…', results: 'Done' }[step]}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: W.muted, fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '22px 24px', overflowY: 'auto', flex: 1 }}>
          {ctxError && <div style={{ color: W.danger, fontSize: '13px', marginBottom: '14px' }}>Couldn't load org data: {ctxError}</div>}

          {step === 'upload' && (
            <div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <button onClick={downloadTemplate} style={{ ...btn('ghost'), fontSize: '12px', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  ⬇ Download template CSV
                </button>
                <a href={`https://timekeep-docs.netlify.app/admin/bulk-import/#${config.docsAnchor}`} target="_blank" rel="noreferrer"
                  style={{ ...btn('ghost'), fontSize: '12px', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none', boxSizing: 'border-box' }}>
                  📘 {config.label} import guide ↗
                </a>
              </div>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
                style={{ border: `2px dashed ${W.border}`, borderRadius: '10px', padding: '40px 20px', textAlign: 'center', cursor: 'pointer', background: W.bg }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>📄</div>
                <div style={{ fontSize: '13px', color: W.bright, fontWeight: '600' }}>Click to choose a file, or drag one here</div>
                <div style={{ fontSize: '11px', color: W.muted, marginTop: '4px' }}>.csv or .xlsx — first row must be column headers</div>
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
              </div>
              {parseErr && <div style={{ color: W.danger, fontSize: '12px', marginTop: '12px' }}>{parseErr}</div>}
              <div style={{ marginTop: '18px', fontSize: '11px', color: W.muted, lineHeight: '1.6' }}>
                Expected columns for {config.label.toLowerCase()}: {config.fields.map(f => f.required ? `${f.label} (required)` : f.label).join(', ')}.
                Column headers don't need to match exactly — you'll map them in the next step.
              </div>
            </div>
          )}

          {step === 'mapping' && (
            <div>
              <div style={{ fontSize: '12px', color: W.muted, marginBottom: '14px' }}>{fileName} — {rawRows.length} row{rawRows.length === 1 ? '' : 's'} found. Confirm what each column maps to.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {headers.map((h, colIndex) => (
                  <div key={colIndex} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${W.border}`, background: W.bg }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', color: W.bright, fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h}</div>
                      <div style={{ fontSize: '11px', color: W.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>e.g. "{rawRows[0]?.[colIndex] || '—'}"</div>
                    </div>
                    <div style={{ fontSize: '14px', color: W.muted }}>→</div>
                    <select style={{ ...sel, minWidth: '200px' }} value={mapping[colIndex] || IGNORE}
                      onChange={e => setMapping(m => ({ ...m, [colIndex]: e.target.value }))}>
                      <option value={IGNORE}>Ignore this column</option>
                      {config.fields.map(f => <option key={f.key} value={f.key}>{f.label}{f.required ? ' *' : ''}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              {mapErr && <div style={{ color: W.danger, fontSize: '12px', marginTop: '12px' }}>{mapErr}</div>}
            </div>
          )}

          {step === 'preview' && (
            <PreviewTable rows={effectiveRows} config={config} onToggleInclude={toggleInclude} onToggleOverride={toggleOverride} />
          )}

          {step === 'importing' && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: W.muted, fontSize: '13px' }}>Importing {includedCount} row{includedCount === 1 ? '' : 's'}…</div>
          )}

          {step === 'results' && results && (
            <ResultsView results={results} config={config} onDownloadErrors={downloadErrorRows} />
          )}
        </div>

        <div style={{ padding: '16px 24px', borderTop: `1px solid ${W.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          {step === 'upload' && <button onClick={onClose} style={btn('ghost')}>Cancel</button>}
          {step === 'mapping' && <>
            <button onClick={() => setStep('upload')} style={btn('ghost')}>← Back</button>
            <button onClick={proceedToPreview} style={btn('primary')} disabled={!ctx}>Continue →</button>
          </>}
          {step === 'preview' && <>
            <button onClick={() => setStep('mapping')} style={btn('ghost')}>← Back</button>
            <button onClick={runImport} style={btn('primary')} disabled={includedCount === 0}>Import {includedCount} row{includedCount === 1 ? '' : 's'}</button>
          </>}
          {step === 'importing' && <div />}
          {step === 'results' && <>
            <div />
            <button onClick={onClose} style={btn('primary')}>Done</button>
          </>}
        </div>
      </div>
    </div>
  );
}

function PreviewTable({ rows, config, onToggleInclude, onToggleOverride }) {
  return (
    <div>
      <div style={{ fontSize: '12px', color: W.muted, marginBottom: '10px' }}>
        Nothing is saved yet — review the rows below, then click Import.
      </div>
      <div style={{ maxHeight: '52vh', overflow: 'auto', border: `1px solid ${W.border}`, borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead style={{ position: 'sticky', top: 0, background: W.surface }}>
            <tr>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: W.muted, borderBottom: `1px solid ${W.border}` }}></th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: W.muted, borderBottom: `1px solid ${W.border}` }}>#</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: W.muted, borderBottom: `1px solid ${W.border}` }}>Row</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: W.muted, borderBottom: `1px solid ${W.border}` }}>Action</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: W.muted, borderBottom: `1px solid ${W.border}` }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.index} style={{ borderBottom: `1px solid ${W.border}` }}>
                <td style={{ padding: '8px 10px' }}>
                  <input type="checkbox" checked={r.include && r.errors.length === 0} disabled={r.errors.length > 0}
                    onChange={() => onToggleInclude(r.index)} />
                </td>
                <td style={{ padding: '8px 10px', color: W.muted }}>{r.index + 2}</td>
                <td style={{ padding: '8px 10px', color: W.bright }}>
                  {(config.previewKeys || config.fields.map(f => f.key)).map(k => r.values[k]).filter(v => v !== null && v !== undefined && v !== '').slice(0, 4).join(' · ') || '—'}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <Badge action={r.displayAction} />
                  {r.matched && r.errors.length === 0 && (
                    <button onClick={() => onToggleOverride(r.index)} style={{ marginLeft: '8px', fontSize: '11px', color: C.accent, background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                      title="Update only overwrites columns present in your file — blank or omitted cells are left untouched, never used to clear existing data.">
                      {r.displayAction === 'update' ? 'switch to skip' : 'switch to update'}
                    </button>
                  )}
                </td>
                <td style={{ padding: '8px 10px', color: r.errors.length ? W.danger : W.muted }}>
                  {[...r.errors, ...r.warnings].join('; ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResultsView({ results, config, onDownloadErrors }) {
  const { summary, rows } = results;
  const errRows = rows.filter(r => r.status === 'error');
  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '18px', flexWrap: 'wrap' }}>
        {[['Created', summary.created, W.success], ['Updated', summary.updated, C.accent], ['Skipped', summary.skipped, W.muted], ['Errors', summary.errors, W.danger]].map(([label, n, color]) => (
          <div key={label} style={{ flex: '1 1 100px', padding: '14px', borderRadius: '8px', border: `1px solid ${W.border}`, background: W.bg, textAlign: 'center' }}>
            <div style={{ fontSize: '22px', fontWeight: '800', color }}>{n}</div>
            <div style={{ fontSize: '11px', color: W.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
          </div>
        ))}
      </div>
      {errRows.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <button onClick={onDownloadErrors} style={btn('ghost')}>Download error rows (CSV)</button>
        </div>
      )}
      <div style={{ maxHeight: '38vh', overflow: 'auto', border: `1px solid ${W.border}`, borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <tbody>
            {rows.map(r => (
              <tr key={r.index} style={{ borderBottom: `1px solid ${W.border}` }}>
                <td style={{ padding: '8px 10px', color: W.muted, width: '40px' }}>{r.index + 2}</td>
                <td style={{ padding: '8px 10px' }}><Badge action={r.status === 'created' ? 'create' : r.status === 'updated' ? 'update' : r.status === 'error' ? 'error' : 'skip'} /></td>
                <td style={{ padding: '8px 10px', color: r.status === 'error' ? W.danger : W.muted }}>{r.statusReason || r.errors.join('; ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
